
import React, { useState } from 'react';
import { motion } from "framer-motion";
import Layout from '@/components/Layout';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobeAmericas, FaUserTie, FaNewspaper, FaHandshake, FaBug } from 'react-icons/fa';

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [generalFormData, setGeneralFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [mediaFormData, setMediaFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: ''
  });
  
  const [investorFormData, setInvestorFormData] = useState({
    name: '',
    email: '',
    company: '',
    investmentLevel: '',
    message: ''
  });
  
  const [techFormData, setTechFormData] = useState({
    name: '',
    email: '',
    issueType: '',
    details: ''
  });

  const handleGeneralSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      setIsSubmitting(false);
      setGeneralFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };
  
  const handleMediaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      toast({
        title: "Media Inquiry Sent!",
        description: "Thanks for your interest. Our media team will contact you shortly.",
      });
      setIsSubmitting(false);
      setMediaFormData({
        name: '',
        email: '',
        organization: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };
  
  const handleInvestorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      toast({
        title: "Investment Inquiry Sent!",
        description: "Thank you for your interest in investing. Our team will reach out with more information.",
      });
      setIsSubmitting(false);
      setInvestorFormData({
        name: '',
        email: '',
        company: '',
        investmentLevel: '',
        message: ''
      });
    }, 1500);
  };
  
  const handleTechSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      toast({
        title: "Technical Issue Submitted",
        description: "Our technical team will investigate and respond as soon as possible.",
      });
      setIsSubmitting(false);
      setTechFormData({
        name: '',
        email: '',
        issueType: '',
        details: ''
      });
    }, 1500);
  };

  return (
    <Layout>
      <div className="space-y-10 py-8">
        {/* Hero Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contact <span className="text-truth">Truth Nexus</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Reach out to our team for inquiries, support, or collaboration opportunities. 
              We're here to answer your questions and help combat misinformation together.
            </p>
          </motion.div>
        </section>
        
        {/* Contact Cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ContactCard 
            icon={<FaEnvelope />} 
            title="Email Us"
            content="wobyebgraphlin90@gmail.com"
            description="For general inquiries and support"
          />
          <ContactCard 
            icon={<FaPhone />} 
            title="Call Us"
            content="+237 677150583"
            description="Available Monday-Friday, 9AM-5PM WAT"
          />
          <ContactCard 
            icon={<FaMapMarkerAlt />} 
            title="Visit Us"
            content="Bamenda, Cameroon"
            description="Our headquarters location"
          />
        </section>
        
        {/* Contact Forms */}
        <section className="mt-12">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full mb-8">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <FaGlobeAmericas className="hidden md:block" /> General Inquiry
              </TabsTrigger>
              <TabsTrigger value="media" className="flex items-center gap-2">
                <FaNewspaper className="hidden md:block" /> Media Contact
              </TabsTrigger>
              <TabsTrigger value="investors" className="flex items-center gap-2">
                <FaHandshake className="hidden md:block" /> Investors
              </TabsTrigger>
              <TabsTrigger value="technical" className="flex items-center gap-2">
                <FaBug className="hidden md:block" /> Technical Support
              </TabsTrigger>
            </TabsList>
            
            <div className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-6 mt-2">
              <TabsContent value="general">
                <h2 className="text-2xl font-bold mb-4">General Inquiry</h2>
                <p className="mb-6 text-gray-300">
                  Have a question or comment? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
                </p>
                
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-1">Your Name</label>
                      <Input
                        id="name"
                        value={generalFormData.name}
                        onChange={(e) => setGeneralFormData({...generalFormData, name: e.target.value})}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-1">Email Address</label>
                      <Input
                        id="email"
                        type="email"
                        value={generalFormData.email}
                        onChange={(e) => setGeneralFormData({...generalFormData, email: e.target.value})}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-1">Subject</label>
                    <Input
                      id="subject"
                      value={generalFormData.subject}
                      onChange={(e) => setGeneralFormData({...generalFormData, subject: e.target.value})}
                      required
                      placeholder="What's your inquiry about?"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1">Your Message</label>
                    <Textarea
                      id="message"
                      value={generalFormData.message}
                      onChange={(e) => setGeneralFormData({...generalFormData, message: e.target.value})}
                      required
                      placeholder="Please provide details about your inquiry..."
                      rows={5}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-truth hover:bg-truth-dark" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="media">
                <h2 className="text-2xl font-bold mb-4">Media Inquiries</h2>
                <p className="mb-6 text-gray-300">
                  Journalists and media professionals, please use this form for interview requests, press information, or other media-related inquiries.
                </p>
                
                <form onSubmit={handleMediaSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="media-name" className="block text-sm font-medium mb-1">Your Name</label>
                      <Input
                        id="media-name"
                        value={mediaFormData.name}
                        onChange={(e) => setMediaFormData({...mediaFormData, name: e.target.value})}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="media-email" className="block text-sm font-medium mb-1">Email Address</label>
                      <Input
                        id="media-email"
                        type="email"
                        value={mediaFormData.email}
                        onChange={(e) => setMediaFormData({...mediaFormData, email: e.target.value})}
                        required
                        placeholder="your@mediaoutlet.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium mb-1">Media Organization</label>
                    <Input
                      id="organization"
                      value={mediaFormData.organization}
                      onChange={(e) => setMediaFormData({...mediaFormData, organization: e.target.value})}
                      required
                      placeholder="Your publication or media outlet"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="media-subject" className="block text-sm font-medium mb-1">Subject</label>
                    <Input
                      id="media-subject"
                      value={mediaFormData.subject}
                      onChange={(e) => setMediaFormData({...mediaFormData, subject: e.target.value})}
                      required
                      placeholder="Interview request, press info, etc."
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="media-message" className="block text-sm font-medium mb-1">Details</label>
                    <Textarea
                      id="media-message"
                      value={mediaFormData.message}
                      onChange={(e) => setMediaFormData({...mediaFormData, message: e.target.value})}
                      required
                      placeholder="Please provide details about your media inquiry, including any deadlines..."
                      rows={5}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-truth hover:bg-truth-dark" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Submit Media Inquiry"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="investors">
                <h2 className="text-2xl font-bold mb-4">Investor Relations</h2>
                <p className="mb-6 text-gray-300">
                  Interested in investing in Truth Nexus? Use this form to request our investor deck and connect with our founder, Wobyeb Graphlain.
                </p>
                
                <div className="bg-truth/10 border border-truth/30 rounded-md p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div>
                      <FaUserTie className="text-truth text-xl mt-1" />
                    </div>
                    <div>
                      <h3 className="text-truth font-medium">Investment Opportunity</h3>
                      <p className="text-sm text-gray-300">
                        Truth Nexus is currently raising Series A funding to scale our AI technologies and expand our global reach.
                        We're looking for strategic investors who share our vision for a misinformation-free digital world.
                      </p>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleInvestorSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="investor-name" className="block text-sm font-medium mb-1">Your Name</label>
                      <Input
                        id="investor-name"
                        value={investorFormData.name}
                        onChange={(e) => setInvestorFormData({...investorFormData, name: e.target.value})}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="investor-email" className="block text-sm font-medium mb-1">Email Address</label>
                      <Input
                        id="investor-email"
                        type="email"
                        value={investorFormData.email}
                        onChange={(e) => setInvestorFormData({...investorFormData, email: e.target.value})}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-1">Company / Fund</label>
                    <Input
                      id="company"
                      value={investorFormData.company}
                      onChange={(e) => setInvestorFormData({...investorFormData, company: e.target.value})}
                      required
                      placeholder="Your company or investment fund"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="investment-level" className="block text-sm font-medium mb-1">Investment Interest Level</label>
                    <select
                      id="investment-level"
                      value={investorFormData.investmentLevel}
                      onChange={(e) => setInvestorFormData({...investorFormData, investmentLevel: e.target.value})}
                      required
                      className="w-full rounded-md border border-gray-700 bg-black/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-truth focus:border-truth"
                    >
                      <option value="">Select an option</option>
                      <option value="Partnership">Partnership </option>
                      <option value="exploring">Exploring opportunities ($1-$500k)</option>
                      <option value="interested">Interested ($500k-$1M)</option>
                      <option value="serious">Serious investor ($1M-$5M)</option>
                      <option value="strategic">Strategic partner ($5M+)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="investor-message" className="block text-sm font-medium mb-1">Additional Information</label>
                    <Textarea
                      id="investor-message"
                      value={investorFormData.message}
                      onChange={(e) => setInvestorFormData({...investorFormData, message: e.target.value})}
                      placeholder="Tell us about your investment focus and any specific questions you have..."
                      rows={4}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-truth hover:bg-truth-dark" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Request Investor Information"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="technical">
                <h2 className="text-2xl font-bold mb-4">Technical Support</h2>
                <p className="mb-6 text-gray-300">
                  Experiencing technical issues with our platform? Let our support team help you resolve them quickly.
                </p>
                
                <form onSubmit={handleTechSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="tech-name" className="block text-sm font-medium mb-1">Your Name</label>
                      <Input
                        id="tech-name"
                        value={techFormData.name}
                        onChange={(e) => setTechFormData({...techFormData, name: e.target.value})}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="tech-email" className="block text-sm font-medium mb-1">Email Address</label>
                      <Input
                        id="tech-email"
                        type="email"
                        value={techFormData.email}
                        onChange={(e) => setTechFormData({...techFormData, email: e.target.value})}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="issue-type" className="block text-sm font-medium mb-1">Issue Type</label>
                    <select
                      id="issue-type"
                      value={techFormData.issueType}
                      onChange={(e) => setTechFormData({...techFormData, issueType: e.target.value})}
                      required
                      className="w-full rounded-md border border-gray-700 bg-black/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-truth focus:border-truth"
                    >
                      <option value="">Select an issue type</option>
                      <option value="login">Login or Account Access</option>
                      <option value="analysis">Content Analysis Tool</option>
                      <option value="source">Source Verification Errors</option>
                      <option value="reporting">Reporting Feature</option>
                      <option value="dashboard">Dashboard Issues</option>
                      <option value="other">Other Technical Problem</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="tech-details" className="block text-sm font-medium mb-1">Issue Details</label>
                    <Textarea
                      id="tech-details"
                      value={techFormData.details}
                      onChange={(e) => setTechFormData({...techFormData, details: e.target.value})}
                      required
                      placeholder="Please describe the issue in detail. Include any error messages, steps to reproduce the problem, and your device/browser information..."
                      rows={5}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-truth hover:bg-truth-dark" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit Support Request"}
                  </Button>
                </form>
              </TabsContent>
            </div>
          </Tabs>
        </section>
        
        {/* Global Offices Map */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold mb-6 text-center">Our Global Offices</h2>
          
          <div className="relative h-96 bg-black/30 backdrop-blur-sm border border-gray-800 rounded-lg overflow-hidden">
            {/* World map placeholder - in a real app, you would integrate a map here */}
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1352&q=80')] bg-no-repeat bg-cover opacity-30"></div>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-lg text-gray-400">Interactive map loading...</p>
            </div>
            
            {/* Office location markers - these would be positioned correctly on a real map */}
            <motion.div 
              className="absolute top-1/3 left-1/4 w-3 h-3 bg-truth rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <div className="absolute top-0 left-0 -ml-2 -mt-2 w-7 h-7 bg-truth/30 rounded-full"></div>
              <div className="absolute top-5 left-5 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                Bamenda, Cameroon
              </div>
            </motion.div>
            
            <motion.div 
              className="absolute top-1/4 left-1/2 w-3 h-3 bg-truth rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ repeat: Infinity, duration: 2, delay: 0.3 }}
            >
              <div className="absolute top-0 left-0 -ml-2 -mt-2 w-7 h-7 bg-truth/30 rounded-full"></div>
              <div className="absolute top-5 left-5 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                Yaoundé, Cameroon
              </div>
            </motion.div>
            
            <motion.div 
              className="absolute top-2/5 left-2/3 w-3 h-3 bg-truth rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ repeat: Infinity, duration: 2, delay: 0.6 }}
            >
              <div className="absolute top-0 left-0 -ml-2 -mt-2 w-7 h-7 bg-truth/30 rounded-full"></div>
              <div className="absolute top-5 left-5 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                Douala, Cameroon
              </div>
            </motion.div>
            
            <motion.div 
              className="absolute top-3/5 left-[45%] w-3 h-3 bg-truth rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ repeat: Infinity, duration: 2, delay: 0.9 }}
            >
              <div className="absolute top-0 left-0 -ml-2 -mt-2 w-7 h-7 bg-truth/30 rounded-full"></div>
              <div className="absolute top-5 left-5 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                Buea, Cameroon
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

const ContactCard = ({ 
  icon, 
  title, 
  content, 
  description 
}: { 
  icon: React.ReactNode, 
  title: string, 
  content: string, 
  description: string 
}) => (
  <Card className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/50 transition-all">
    <CardContent className="p-6 flex flex-col items-center text-center">
      <div className="text-truth text-3xl mb-4 bg-truth/10 w-16 h-16 rounded-full flex items-center justify-center">
        {icon}
      </div>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-truth font-medium mb-2">{content}</p>
      <p className="text-sm text-gray-400">{description}</p>
    </CardContent>
  </Card>
);

export default Contact;
